import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, Check, RotateCcw, Search, DollarSign } from 'lucide-react';

const API = '/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const PBFeaturesPanel = () => {
  const [features, setFeatures] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pb-features`, { params: { status: filter } });
      setFeatures(r.data.features || []);
      setStats(r.data.stats || null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load PB features');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [filter]);

  const toggleDelivered = async (feat) => {
    const next = !feat.delivered;
    setFeatures((v) => v.map((x) => (x.id === feat.id ? { ...x, delivered: next, delivered_at: next ? new Date().toISOString() : null } : x)));
    try {
      await axios.post(`${API}/admin/pb-features/${feat.id}/toggle`, { delivered: next });
      toast.success(next ? `Marked delivered for ${feat.client_name}` : `Reset to pending`);
      load();
    } catch (e) {
      toast.error('Failed to update feature');
      setFeatures((v) => v.map((x) => (x.id === feat.id ? { ...x, delivered: feat.delivered, delivered_at: feat.delivered_at } : x)));
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return features;
    const q = search.trim().toLowerCase();
    return features.filter((f) =>
      (f.client_name || '').toLowerCase().includes(q)
      || (f.client_email || '').toLowerCase().includes(q)
      || (f.booking_number || '').toLowerCase().includes(q)
    );
  }, [features, search]);

  return (
    <div
      className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-3 sm:p-6"
      data-testid="pb-features-panel"
    >
      <div className="mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 flex items-center gap-2">
          <Mic className="w-6 h-6 text-fuchsia-400" /> PB Slimeyy · Feature Verse Tracker
        </h2>
        <p className="text-xs sm:text-sm text-gray-400">
          Every $150 site-only feature verse from PB. Toggle "Delivered" once he sends the mixed verse.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-fuchsia-900/20 border border-fuchsia-500/30 rounded-lg p-3">
            <div className="text-[10px] text-fuchsia-300 uppercase tracking-wide">Total</div>
            <div className="text-2xl font-bold text-white" data-testid="stat-total">{stats.total}</div>
          </div>
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3">
            <div className="text-[10px] text-amber-300 uppercase tracking-wide">Pending</div>
            <div className="text-2xl font-bold text-white" data-testid="stat-pending">{stats.pending}</div>
          </div>
          <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-[10px] text-emerald-300 uppercase tracking-wide">Delivered</div>
            <div className="text-2xl font-bold text-white" data-testid="stat-delivered">{stats.delivered}</div>
          </div>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
            <div className="text-[10px] text-green-300 uppercase tracking-wide flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Revenue
            </div>
            <div className="text-2xl font-bold text-white" data-testid="stat-revenue">${stats.revenue}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex gap-1 bg-purple-900/20 border border-purple-500/20 rounded-lg p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'pending', label: 'Pending' },
            { id: 'delivered', label: 'Delivered' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`pb-filter-${f.id}`}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
                filter === f.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-purple-800/40'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, booking #…"
            data-testid="pb-search"
            className="w-full pl-9 pr-3 py-2 bg-purple-900/20 border border-purple-500/20 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-purple-400"
          />
        </div>
        <button
          onClick={load}
          data-testid="pb-refresh"
          className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-3 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-purple-500/20 text-left">
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs">Client</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs hidden md:table-cell">Booking</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs hidden md:table-cell">Session</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs hidden lg:table-cell">Requested</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs">Status</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-6 text-center text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No PB feature requests {filter !== 'all' ? `(${filter})` : ''} yet.
                  </td>
                </tr>
              ) : filtered.map((f) => (
                <tr
                  key={f.id}
                  className="border-b border-purple-500/10 hover:bg-purple-500/5"
                  data-testid={`pb-feature-row-${f.id}`}
                >
                  <td className="py-2 px-2 text-gray-300">
                    <div className="text-sm font-semibold text-white">{f.client_name || '—'}</div>
                    <div className="text-[10px] text-gray-500">{f.client_email}</div>
                    {f.client_phone && <div className="text-[10px] text-gray-500">{f.client_phone}</div>}
                  </td>
                  <td className="py-2 px-2 text-gray-300 hidden md:table-cell text-xs font-mono">{f.booking_number || '—'}</td>
                  <td className="py-2 px-2 text-gray-300 hidden md:table-cell text-xs">
                    <div>{f.session_date}</div>
                    <div className="text-gray-500">{f.session_time}</div>
                  </td>
                  <td className="py-2 px-2 text-gray-400 hidden lg:table-cell text-xs">{formatDate(f.created_at)}</td>
                  <td className="py-2 px-2">
                    {f.delivered ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                        <Check className="w-3 h-3" /> DELIVERED
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        PENDING
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <button
                      onClick={() => toggleDelivered(f)}
                      data-testid={`pb-toggle-${f.id}`}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                        f.delivered
                          ? 'bg-gray-700/50 hover:bg-gray-700 text-gray-300 border border-gray-600'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      }`}
                    >
                      {f.delivered ? (
                        <><RotateCcw className="w-3 h-3" /> Undo</>
                      ) : (
                        <><Check className="w-3 h-3" /> Mark Delivered</>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PBFeaturesPanel;
